// src/pages/NationwideWelcome.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import track from "../../lib/analytics/events";
import { Loader2 } from "lucide-react";
import {
  getAuth,
  onAuthStateChanged,
  signInWithCustomToken,
} from "firebase/auth";
import API_CONFIG from "../../lib/ApiConfig";
import { OwnliButton } from "../ownli/OwnliScreenWrapper";

const looksLikeJwt = (t) => typeof t === "string" && t.split(".").length === 3;

const NationwideWelcome = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = getAuth();

  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [error, setError] = useState(null);
  const [userName, setUserName] = useState("");
  const [alreadySignedIn, setAlreadySignedIn] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setAuthInitialized(true); // Auth has initialized (user may be null or not)
      if (user) {
        setAlreadySignedIn(true);
        setUserName(user.displayName || user.email || "");
      }
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    if (!authInitialized) return; // Wait for auth to initialize

    const guidFromUrl = searchParams.get("guid");
    // Always re-run exchange + bootstrap when a guid is present, even if
    // the user is already signed in to Firebase. Bootstrap is idempotent
    // (getOrCreateUser finds existing user; matchUser only flips the row
    // when status != Imported), so re-running is safe and heals users
    // whose first bootstrap half-completed (Ownli user created but
    // NationwideUser still Pending).
    // const currentUser = auth.currentUser;
    // if (currentUser) {
    //   navigate("/welcome", { state: { fromNationwide: true }, replace: true });
    //   return;
    // }

    track.activationOpened({ guid_present: Boolean(guidFromUrl) });

    if (!guidFromUrl) {
      setStatus("error");
      setError(
        "No verification code found in the link. Please check your email and try again."
      );
      return;
    }

    (async () => {
      try {
        setStatus("verifying");
        setError(null);

        // 1) Exchange: Nationwide GUID -> Firebase CUSTOM token (+ optional metadata)
        const url = API_CONFIG.getInternalUrl(
          `/api/nationwide/exchange?guid=${encodeURIComponent(guidFromUrl)}`
        );
        const exchangeRes = await fetch(url, {
          method: "GET",
          cache: "no-store",
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        });

        const exchangeCt = exchangeRes.headers.get("content-type") || "";
        const exchangeData = exchangeCt.includes("application/json")
          ? await exchangeRes.json()
          : { message: await exchangeRes.text() };

        if (!exchangeRes.ok) {
          track.activationExchangeFailed({
            status: exchangeRes.status,
            reason: exchangeData?.message || "non_ok_response",
          });
          throw new Error(exchangeData?.message || "Exchange failed");
        }

        track.activationExchangeSucceeded({ status: exchangeRes.status });

        // Align these keys to your NationwideAuthResponse
        const firebaseCustomToken =
          exchangeData.sessionToken ||
          exchangeData.firebaseToken ||
          exchangeData.firebaseCustomToken ||
          exchangeData.customToken;

        if (!firebaseCustomToken) {
          throw new Error("Exchange did not return a Firebase custom token.");
        }

        // 2) Client exchange: custom token -> Firebase session (user)
        const cred = await signInWithCustomToken(auth, firebaseCustomToken);
        const firebaseUser = cred.user;

        // 3) Get Firebase ID token (JWT) to call Ownli APIs
        const firebaseIdToken = await firebaseUser.getIdToken(true);
        if (!looksLikeJwt(firebaseIdToken)) {
          throw new Error(
            "Expected a Firebase ID token (JWT) after sign-in, but got something else."
          );
        }
        sessionStorage.setItem("firebaseIdToken", firebaseIdToken);

        // 4) Bootstrap: create/sync Ownli user + mark NationwideUser as matched
        const bootstrapUrl = API_CONFIG.getInternalUrl(
          `/api/nationwide/bootstrap?guid=${encodeURIComponent(guidFromUrl)}`
        );
        const bootstrapRes = await fetch(bootstrapUrl, {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${firebaseIdToken}`,
            "Content-Type": "application/json",
          },
        });

        if (!bootstrapRes.ok) {
          track.activationBootstrapFailed({ status: bootstrapRes.status });
          throw new Error("Failed to create your account. Please try again.");
        }

        const serverUser = await bootstrapRes.json();
        track.activationBootstrapSucceeded({ status: bootstrapRes.status });

        // 5) Store useful session info
        if (serverUser) {
          sessionStorage.setItem("userData", JSON.stringify(serverUser));
        }
        sessionStorage.setItem(
          "verificationData",
          JSON.stringify({
            method: "nationwide_magic_link",
            userId: serverUser?.id || firebaseUser.uid,
            verifiedAt: new Date().toISOString(),
            source: "nationwide",
          })
        );

        // 6) Analytics — identify the user so subsequent events attach to them
        track.identify(serverUser?.id || firebaseUser.uid, {
          email: firebaseUser.email || exchangeData.email,
          name:
            firebaseUser.displayName ||
            serverUser?.firstName ||
            exchangeData.firstName,
          source: "nationwide",
          activated_at: new Date().toISOString(),
        });

        // 7) UI success + redirect
        setUserName(
          firebaseUser.displayName ||
            serverUser?.firstName ||
            exchangeData.firstName ||
            exchangeData.email ||
            ""
        );
        setStatus("success");
      } catch (err) {
        console.error("Nationwide authentication error:", err);
        track.errorShown({
          type: "activation_failed",
          screen: "activation",
          message: err?.message,
        });
        setStatus("error");
        setError(
          err?.message || "Failed to verify your link. Please try again."
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authInitialized, searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col">
      {/* Header — matches Navbar on /welcome */}
      <header className="bg-white shadow-sm py-3 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-center">
          <div className="w-52 h-16 bg-white flex items-center justify-center overflow-hidden">
            <img
              src="/logos/ownli_logo.png"
              alt="Ownli"
              className="w-full h-full object-contain"
            />
          </div>
        </div>
      </header>

      <div className="max-w-md w-full mx-auto flex-1 flex items-center justify-center px-4">
        {status === "verifying" && (
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="mb-6 flex justify-center">
              <Loader2 className="h-12 w-12 animate-spin text-crimsonGlory" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              Verifying Your Information
            </h2>
            <p className="text-gray-600">
              Please wait while we securely verify your Nationwide account...
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="mb-6">
              <svg
                className="mx-auto h-16 w-16 text-crimsonGlory"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              {userName ? `Welcome, ${userName}!` : "Account Verified"}
            </h2>

            <p className="text-gray-600 mb-6">
              Your Nationwide account has been verified successfully.
            </p>

            <button
              onClick={() => {
                track.activationContinueClicked({ from: "success" });
                navigate("/welcome", {
                  state: { fromNationwide: true },
                  replace: true,
                });
              }}
              className="w-full bg-crimsonGlory text-white py-3 px-4 rounded-lg hover:bg-blue-800 transition-colors font-medium"
            >
              Continue
            </button>
          </div>
        )}

        {status === "error" && (
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="mb-6 text-center">
              <svg
                className="mx-auto h-16 w-16 text-crimsonGlory"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4 text-center">
              {!searchParams.get("guid")
                ? "No Verification Link Found"
                : "Verification Link Issue"}
            </h2>

            <p className="text-gray-600 mb-6 text-center">
              {!searchParams.get("guid")
                ? "We didn't receive a verification link. You can proceed and log in manually to access your account."
                : "The verification link may have expired or is invalid. You can proceed and log in manually to access your account."}
            </p>

            <OwnliButton
              onClick={() => {
                track.activationContinueClicked({ from: "error" });
                navigate("/welcome", { replace: true });
              }}
            >
              Continue
            </OwnliButton>
          </div>
        )}
      </div>

      {/* Footer — matches MainLayout */}
      <footer className="flex justify-between items-center px-6 py-4 mt-auto text-gray-500 text-sm">
        <p className="flex items-center space-x-2">
          <span>Need help?</span>
          <button
            onClick={() => window.open("https://support.ownli.co/hc/en-us/requests/new", "_blank")}
            className="text-blue-500 hover:text-blue-600 underline"
          >
            Contact Us.
          </button>
        </p>
        <p className="flex items-center space-x-2">
          <span>Powered by</span>
          <img src="/logo.jpeg" alt="Ownli logo" className="h-4" />
        </p>
      </footer>
    </div>
  );
};

export default NationwideWelcome;
